import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { ThemeConfig } from '../../../core/helpers/theme-config'
import { minQuantity, stepping, unitPrice, upwardsQuantity } from '../../../core/helpers/prices';

import { Product } from '../../../core/models/product';
import { Price } from '../../../core/models/product/price';
import { AddCartEvent, CartProduct } from '../../../cart/models/cart-models';
import { hasProps } from '../../../core/helpers/object';
import { canBuyProduct } from '../../../core/helpers/product';
import { ToggleFavoriteEvent } from '../../../catalog/models/favorite-product'

import { Image } from 'angular-modal-gallery';
import { ApiImage } from '../../../core/models/product/image';
import { QuantityChangedEvent } from '../../models/quantity-changed-event';
import chaining from '../../../core/helpers/chaining';
import { Setting } from '../../../core/models/setting';
import { CombinedFacets } from '../../../facets/models/facet';
import { TileFacet } from '../../../catalog/models/tile-facet';
import findTileFacets from '../../../core/helpers/tile-facet';

const hasDetails = ThemeConfig.get<boolean>('catalog', 'has-details');
const isTileOrderable = ThemeConfig.get<boolean>('catalog', 'is-tile-orderable');

@Component({
  selector: 'app-product-tile',
  templateUrl: './template.html',
  styleUrls: ['./styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductTileComponent implements OnChanges {
  startQuantity = 0
  quantity: number
  imgUrl: string

  @Input() product: Product;
  @Input() cartProduct: CartProduct
  @Input() combinedFacets: CombinedFacets
  @Input() isFavorite: boolean
  @Input() catalogItemPageSettings: Setting
  @Input() quantityPicker: string
  @Input() allowOutOfStockOrdering: boolean

  @Output() onDetails = new EventEmitter<Product>();
  @Output() onAddCart = new EventEmitter<AddCartEvent>();
  @Output() onFavoriteToggle = new EventEmitter<ToggleFavoriteEvent>()

  productImage: Image

  ngOnChanges() {
    this.getProductImage()
    this.getActualQuantity(this.cartProduct && this.cartProduct.quantity)
  }

  getProductImage() {
    if (this.product && this.product.assets && this.product.assets.imgs)
      this.productImage = new Image(
        +new Date(),
        {
          img: (this.product.assets.imgs
            .find(img => img.type === 'detail') || {} as ApiImage )
            .src
        }
      )
  }

  getActualQuantity(cartQuantity) {
    this.startQuantity = cartQuantity > 0
      ? cartQuantity
      : 0
  }

  get hasPrice(): Boolean {
    return !!(this.product && this.product.price)
  }

  get productFacets(): TileFacet[] {
    return findTileFacets(chaining(this.catalogItemPageSettings, 'showFacets'),
      chaining(this.product, 'facets'), this.combinedFacets)
  }

  get name(): string {
    return this.product.name || 'No Name';
  }

  get code(): string {
    return this.product.code;
  }

  get displayCode(): string {
    return this.product.displayCode;
  }

  get prices(): Price[] {
    return this.product.price.prices;
  }

  get currentPrice(): Price {
    return unitPrice(this.product, this.actualQuantity || this.minQuantity)
  }

  get stepping(): number {
    return stepping(this.product)
  }

  get actualStepping(): number {
    return this.product.price.stepping
  }

  get minQuantity(): number {
    return minQuantity(this.product)
  }

  get actualQuantity(): number {
    return upwardsQuantity(this.product, this.quantity || this.startQuantity)
  }

  get hasMultiplePrices(): boolean {
    return chaining(this.prices, 'length') > 1;
  }

  get hasDetails(): boolean {
    return hasDetails;
  }

  get isInCart(): boolean {
    return (this.actualQuantity > 0);
  }

  get isTileOrderable(): boolean {
    return isTileOrderable;
  }

  get canBuy(): boolean {
    return canBuyProduct(this.product, this.allowOutOfStockOrdering);
  }

  get showQuantityPickerAnimated(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase() === 'animated')
  }

  get quantityPickerRelative(): boolean {
    return (this.quantityPicker && this.quantityPicker.toLowerCase() === 'relative')
  }

  get isProductInBackorder(): boolean {
    return (this.product && this.product.backorderInfo && this.product.backorderInfo.totalInBackorder > 0)
  }

  get isProductPreviouslyOrdered(): boolean {
    return (this.product && this.product.totalPreviouslyOrdered && this.product.totalPreviouslyOrdered > 0)
  }

  quantityChanged(event: QuantityChangedEvent) {
    this.startQuantity = chaining(event, 'quantity');
    this.onAddCart.emit({
      product: chaining(this, 'product._id'),
      quantity: chaining(event, 'quantity')
    });
  }

  openDetails() {
    if(!hasDetails)
      return;

    this.onDetails.emit(this.product);
  }

  toggleFavorite(e: MouseEvent) {
    e.stopPropagation()
    this.onFavoriteToggle.emit({
      isFavorite: this.isFavorite,
      product: this.product
    })
  }

  onImageError(url: string) {
    this.imgUrl = url
  }
}
